import { initInfiniteListings } from "./infinite-listings.js";
import { initPropertyReturn } from "./property-return.js";

declare global {
  interface Window {
    ncePlatform?: { restUrl: string; restNonce: string; dataMode: string; demoLabel: string; currency: string; locale: string; mapStyle: string; deferMap?: boolean; mapScriptUrl?: string; mapStyleUrl?: string };
  }
}

const config = window.ncePlatform;
// Keep the article within immediate reach on phones; visitors can expand its contents.
document.querySelectorAll<HTMLDetailsElement>(".nce-editorial-toc").forEach((contents) => {
  contents.open = !window.matchMedia("(max-width: 900px)").matches;
});
type EnquiryReceipt = { data: { message?: string; duplicate?: boolean; whatsappUrl?: string; sharedAwaitingClaim?: boolean } };
function attribution(): Record<string, string | null> {
  const params = new URLSearchParams(window.location.search);
  const stored = (() => { try { return JSON.parse(sessionStorage.getItem("nceAttribution") || "{}") as Record<string, string>; } catch { return {}; } })();
  const values: Record<string, string | null> = {
    referrer: document.referrer || stored.referrer || null,
    utmSource: params.get("utm_source") || stored.utmSource || null,
    utmMedium: params.get("utm_medium") || stored.utmMedium || null,
    utmCampaign: params.get("utm_campaign") || stored.utmCampaign || null,
    utmTerm: params.get("utm_term") || stored.utmTerm || null,
    utmContent: params.get("utm_content") || stored.utmContent || null,
    gclid: params.get("gclid") || stored.gclid || null,
  };
  try { sessionStorage.setItem("nceAttribution", JSON.stringify(values)); } catch { /* Storage can be unavailable. */ }
  return values;
}

async function sendEnquiry(payload: Record<string, unknown>): Promise<EnquiryReceipt> {
  if (!config) throw new Error("Platform configuration is unavailable");
  const response = await fetch(`${config.restUrl}/enquiries`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-WP-Nonce": config.restNonce, "Idempotency-Key": crypto.randomUUID() },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => null) as EnquiryReceipt | { message?: string } | null;
  if (!response.ok) throw new Error(body && "message" in body ? body.message || "Enquiry failed" : "Enquiry failed");
  return body as EnquiryReceipt;
}

function initEnquiries(): void {
  attribution();
  document.querySelectorAll<HTMLFormElement>("[data-nce-enquiry]").forEach((form) => {
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const status = form.querySelector<HTMLElement>("[data-form-status]");
      if (!form.reportValidity()) return;
      const data = new FormData(form);
      if (data.get("website")) return;
      const submit = form.querySelector<HTMLButtonElement>("button[type=submit]");
      if (submit) submit.disabled = true;
      if (status) { status.dataset.status = "loading"; status.textContent = "Sending your enquiry…"; }
      const optional = (name: string): string | undefined => String(data.get(name) || "").trim() || undefined;
      const detailLines = Array.from(form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[data-enquiry-detail]"))
        .map((field) => {
          const value = String(field.value || "").trim();
          const label = field.dataset.detailLabel || field.name;
          return value ? `${label}: ${value}` : "";
        })
        .filter(Boolean);
      const freeMessage = String(data.get("message") || "").trim();
      const compiledMessage = [...detailLines, ...(freeMessage ? [`Additional notes: ${freeMessage}`] : [])].join("\n");
      try {
        const receipt = await sendEnquiry({
          type: String(data.get("type") || "general"), name: String(data.get("name") || ""),
          ...(optional("email") ? { email: optional("email") } : {}), ...(optional("phone") ? { phone: optional("phone") } : {}),
          message: compiledMessage || freeMessage || "Listing enquiry", ...(optional("listingId") ? { listingId: optional("listingId") } : {}),
          ...(optional("estateId") ? { estateId: optional("estateId") } : {}), ...(optional("developmentId") ? { developmentId: optional("developmentId") } : {}),
          ...(optional("agentId") ? { agentId: optional("agentId") } : {}), pageUrl: window.location.href,
          formVariant: String(data.get("formVariant") || "default"),
          consent: { accepted: true, languageVersion: "privacy-v1", marketingOptIn: data.get("marketingOptIn") === "1" },
          attribution: attribution(), honeypot: "",
        });
        form.reset();
        if (status) { status.dataset.status = "success"; status.textContent = receipt.data.message || "Thank you. A specialist will be in touch."; }
        window.dispatchEvent(new CustomEvent("nce:enquiry", { detail: { type: data.get("type"), duplicate: receipt.data.duplicate } }));
      } catch {
        if (status) { status.dataset.status = "error"; status.textContent = "Your enquiry could not be sent. Please check your details or try again shortly."; }
      } finally { if (submit) submit.disabled = false; }
    });
  });
}

function storedList(key: string): string[] { try { const value = JSON.parse(localStorage.getItem(key) || "[]"); return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []; } catch { return []; } }
function renderFavouriteButtons(): void {
  const values = storedList("nceFavourites");
  document.querySelectorAll<HTMLButtonElement>("[data-nce-favourite]").forEach((button) => {
    const active = values.includes(button.dataset.nceFavourite || "");
    const label = button.querySelector("span");
    button.setAttribute("aria-pressed", String(active));
    button.setAttribute("aria-label", active ? "Remove saved property" : "Save property");
    if (label) label.textContent = active ? "Saved" : "Save";
  });
  document.querySelectorAll<HTMLElement>("[data-nce-saved-count]").forEach((count) => { count.textContent = String(values.length); });
}
function initAccountLite(): void {
  const current = document.querySelector<HTMLElement>("[data-nce-property-reference]")?.dataset.ncePropertyReference;
  if (current) { const recent = [current, ...storedList("nceRecentlyViewed").filter((item) => item !== current)].slice(0, 20); try { localStorage.setItem("nceRecentlyViewed", JSON.stringify(recent)); } catch { /* Local privacy storage is optional. */ } }
  renderFavouriteButtons();
  document.addEventListener("click", (event) => {
    const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("[data-nce-favourite]") : null;
    if (!button) return;
    const reference = button.dataset.nceFavourite || "";
    if (!reference) return;
    const values = storedList("nceFavourites");
    const next = values.includes(reference) ? values.filter((item) => item !== reference) : [reference, ...values].slice(0, 100);
    try { localStorage.setItem("nceFavourites", JSON.stringify(next)); } catch { /* Local privacy storage is optional. */ }
    renderFavouriteButtons();
    window.dispatchEvent(new CustomEvent("nce:favourite", { detail: { reference, saved: next.includes(reference) } }));
  });
  document.querySelector<HTMLButtonElement>("[data-save-search]")?.addEventListener("click", () => { const status = document.querySelector<HTMLElement>("[data-save-search-status]"); try { const existing = JSON.parse(localStorage.getItem("nceSavedSearches") || "[]") as Array<{ query: string; savedAt: string }>; const query = window.location.search || "?"; const next = [{ query, savedAt: new Date().toISOString() }, ...existing.filter((item) => item.query !== query)].slice(0, 10); localStorage.setItem("nceSavedSearches", JSON.stringify(next)); if (status) status.textContent = "Search saved. Open Saved homes to return to it or request matching-property alerts below."; window.dispatchEvent(new CustomEvent("nce:saved-search")); } catch { if (status) { status.dataset.status = "error"; status.textContent = "This browser did not allow local storage."; } } });

}

type SavedSearch = { query: string; savedAt: string };
function initShortlist(): void {
  const page = document.querySelector<HTMLElement>("[data-nce-shortlist]");
  if (!page) return;
  const catalogue = page.querySelector<HTMLElement>("[data-nce-shortlist-catalogue]");
  const results = page.querySelector<HTMLElement>("[data-nce-shortlist-results]");
  const empty = page.querySelector<HTMLElement>("[data-nce-shortlist-empty]");
  const count = page.querySelector<HTMLElement>("[data-nce-shortlist-count]");
  const status = page.querySelector<HTMLElement>("[data-nce-shortlist-status]");
  const share = page.querySelector<HTMLButtonElement>("[data-nce-share-shortlist]");
  const compare = page.querySelector<HTMLButtonElement>("[data-nce-compare-shortlist]");
  const comparison = page.querySelector<HTMLElement>("[data-nce-property-comparison]");
  const comparisonTable = page.querySelector<HTMLElement>("[data-nce-comparison-table]");
  const selected = new Set<string>();
  const sharedReferences = Array.from(new URLSearchParams(window.location.search).get("properties")?.split(",") || []).map((value) => value.trim()).filter(Boolean).slice(0, 12);
  const allCards = Array.from(catalogue?.querySelectorAll<HTMLElement>(".nce-listing-card[data-property-reference]") || []);

  const visibleReferences = () => sharedReferences.length ? sharedReferences : storedList("nceFavourites");
  const syncCompareButton = () => {
    if (!compare) return;
    compare.disabled = selected.size < 2 || selected.size > 4;
    compare.textContent = selected.size ? `Compare selected (${selected.size})` : "Compare selected";
  };
  const makeComparison = () => {
    if (!comparison || !comparisonTable) return;
    const cards = Array.from(results?.querySelectorAll<HTMLElement>(".nce-listing-card") || []).filter((card) => selected.has(card.dataset.propertyReference || ""));
    if (cards.length < 2) return;
    const table = document.createElement("table");
    const body = document.createElement("tbody");
    const rows: Array<[string, (card: HTMLElement) => string]> = [
      ["Property", (card) => card.dataset.title || "Property"], ["Estate", (card) => card.dataset.estate || "—"],
      ["Price", (card) => card.dataset.priceLabel || "—"], ["Type", (card) => card.dataset.propertyTypeLabel || "—"],
      ["Bedrooms", (card) => Number(card.dataset.bedrooms || 0) ? card.dataset.bedrooms || "—" : "—"],
      ["Bathrooms", (card) => Number(card.dataset.bathrooms || 0) ? card.dataset.bathrooms || "—" : "—"],
      ["Size", (card) => Number(card.dataset.size || 0) ? `${Number(card.dataset.size).toLocaleString("en-ZA")} m²` : "—"],
    ];
    for (const [label, value] of rows) {
      const row = document.createElement("tr"); const heading = document.createElement("th"); heading.scope = "row"; heading.textContent = label; row.append(heading);
      cards.forEach((card) => { const cell = document.createElement("td"); cell.textContent = value(card); row.append(cell); }); body.append(row);
    }
    table.append(body); comparisonTable.replaceChildren(table); comparison.hidden = false; comparison.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const render = () => {
    if (!results || !catalogue) return;
    const references = visibleReferences();
    selected.forEach((reference) => { if (!references.includes(reference)) selected.delete(reference); });
    const grid = document.createElement("div"); grid.className = "nce-card-grid"; grid.dataset.listingResults = "";
    allCards.filter((card) => references.includes(card.dataset.propertyReference || "")).forEach((source) => {
      const card = source.cloneNode(true) as HTMLElement;
      const reference = card.dataset.propertyReference || "";
      const compareControl = document.createElement("label"); compareControl.className = "nce-listing-card__compare";
      const checkbox = document.createElement("input"); checkbox.type = "checkbox"; checkbox.checked = selected.has(reference); checkbox.setAttribute("aria-label", `Compare ${card.dataset.title || "property"}`);
      const label = document.createElement("span"); label.textContent = "Compare"; compareControl.append(checkbox, label); card.append(compareControl);
      checkbox.addEventListener("change", () => { if (checkbox.checked) selected.add(reference); else selected.delete(reference); syncCompareButton(); });
      grid.append(card);
    });
    results.replaceChildren(grid); renderFavouriteButtons();
    const shown = grid.childElementCount;
    if (count) count.textContent = `${shown} ${shown === 1 ? "saved home" : "saved homes"}`;
    if (empty) empty.hidden = shown > 0;
    results.hidden = shown === 0;
    if (share) share.disabled = shown === 0;
    syncCompareButton();
  };
  const shareShortlist = async () => {
    const references = visibleReferences(); if (!references.length) return;
    const url = new URL(window.location.href); url.search = ""; url.searchParams.set("properties", references.join(","));
    try {
      if (navigator.share) await navigator.share({ title: "North Coast property shortlist", text: "A North Coast Estates property shortlist", url: url.toString() });
      else { await navigator.clipboard.writeText(url.toString()); if (status) status.textContent = "Shareable shortlist link copied."; }
    } catch (error) { if (!(error instanceof DOMException && error.name === "AbortError") && status) status.textContent = "The shortlist link could not be shared from this browser."; }
  };
  share?.addEventListener("click", () => { void shareShortlist(); }); compare?.addEventListener("click", makeComparison);
  page.querySelector<HTMLButtonElement>("[data-nce-close-comparison]")?.addEventListener("click", () => { if (comparison) comparison.hidden = true; });
  window.addEventListener("nce:favourite", render); render();

  const searchList = page.querySelector<HTMLElement>("[data-nce-saved-search-list]");
  const renderSearches = () => {
    if (!searchList) return;
    let searches: SavedSearch[] = []; try { const value = JSON.parse(localStorage.getItem("nceSavedSearches") || "[]"); if (Array.isArray(value)) searches = value; } catch { /* Optional storage. */ }
    searchList.replaceChildren();
    if (!searches.length) { const message = document.createElement("p"); message.textContent = "No searches saved on this device yet."; searchList.append(message); return; }
    searches.forEach((saved) => {
      const row = document.createElement("article"); const copy = document.createElement("div"); const title = document.createElement("strong"); const meta = document.createElement("span");
      const params = new URLSearchParams(saved.query); const parts = Array.from(params.entries()).filter(([key]) => !["sort", "page"].includes(key)).map(([key, value]) => `${key.replace(/([A-Z])/g, " $1")}: ${value.replaceAll("_", " ")}`);
      title.textContent = parts.length ? parts.join(" · ") : "All North Coast property"; meta.textContent = `Saved ${new Date(saved.savedAt).toLocaleDateString("en-ZA")}`; copy.append(title, meta);
      const actions = document.createElement("div"); const open = document.createElement("a"); open.href = `${window.location.origin}/properties/${saved.query}#property-results`; open.textContent = "Open search";
      const remove = document.createElement("button"); remove.type = "button"; remove.textContent = "Remove"; remove.addEventListener("click", () => { try { localStorage.setItem("nceSavedSearches", JSON.stringify(searches.filter((item) => item.query !== saved.query))); } catch { /* Optional storage. */ } renderSearches(); });
      actions.append(open, remove); row.append(copy, actions); searchList.append(row);
    });
  };
  window.addEventListener("nce:saved-search", renderSearches); renderSearches();
}

function initDeferredMap(): void {
  if (!config?.deferMap || !config.mapScriptUrl || !document.querySelector("[data-nce-map]")) return;
  let loading = false;
  const load = () => {
    if (loading) return; loading = true;
    if (config.mapStyleUrl && !document.querySelector('link[data-nce-deferred-map-style]')) { const link = document.createElement("link"); link.rel = "stylesheet"; link.href = config.mapStyleUrl; link.dataset.nceDeferredMapStyle = ""; document.head.append(link); }
    const script = document.createElement("script"); script.src = config.mapScriptUrl || ""; script.defer = true; script.dataset.nceDeferredMapScript = ""; document.body.append(script);
  };
  const target = document.querySelector<HTMLElement>("[data-nce-map]");
  if (!target || !("IntersectionObserver" in window)) { load(); return; }
  const observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); load(); } }, { rootMargin: "500px 0px" }); observer.observe(target);
}
function initPropertySearch(): void {
  document.querySelectorAll<HTMLFormElement>("[data-nce-property-search]").forEach((form) => {
    const estate = form.querySelector<HTMLSelectElement>("[data-search-estate]");
    const development = form.querySelector<HTMLSelectElement>("[data-search-development]");
    const count = form.querySelector<HTMLElement>("[data-filter-count]");
    const summary = form.querySelector<HTMLElement>("[data-filter-summary]");
    const minimumPrice = form.querySelector<HTMLSelectElement>('[data-price-select="min"]');
    const maximumPrice = form.querySelector<HTMLSelectElement>('[data-price-select="max"]');
    const moreToggle = form.querySelector<HTMLButtonElement>("[data-search-more-toggle]");
    const morePanel = form.querySelector<HTMLElement>("[data-search-more-panel]");
    const resultsSort = form.id ? document.querySelector<HTMLSelectElement>(`[data-results-sort][form="${form.id}"]`) : null;
    const salePrices = [500000, 750000, 1000000, 1250000, 1500000, 2000000, 2500000, 3000000, 4000000, 5000000, 7500000, 10000000, 15000000, 20000000, 30000000, 50000000];
    const rentalPrices = [5000, 7500, 10000, 12500, 15000, 20000, 25000, 30000, 40000, 50000, 65000, 80000, 100000, 150000];
    const priceFormatter = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 });
    const constrainPriceRange = () => {
      if (!minimumPrice || !maximumPrice) return;
      const minimum = Number(minimumPrice.value || 0);
      Array.from(maximumPrice.options).forEach((option) => { option.disabled = Boolean(option.value) && Number(option.value) < minimum; });
      if (maximumPrice.value && Number(maximumPrice.value) < minimum) maximumPrice.value = "";
    };
    const rebuildPriceRanges = (mode: string) => {
      if (!minimumPrice || !maximumPrice) return;
      const prices = mode === "rental" ? rentalPrices : salePrices;
      const rebuild = (select: HTMLSelectElement, placeholder: string) => {
        const previous = Number(select.value || 0);
        select.replaceChildren(new Option(placeholder, ""), ...prices.map((price) => new Option(priceFormatter.format(price), String(price))));
        select.value = prices.includes(previous) ? String(previous) : "";
      };
      rebuild(minimumPrice, "Min price");
      rebuild(maximumPrice, "Max price");
      constrainPriceRange();
    };
    const updateDevelopments = () => {
      if (!development) return;
      const selectedEstate = estate?.value || "";
      for (const option of Array.from(development.options)) {
        if (!option.value) continue;
        const visible = !selectedEstate || option.dataset.estate === selectedEstate;
        option.hidden = !visible;
        option.disabled = !visible;
        if (!visible && option.selected) development.value = "";
      }
    };
    const updateCount = () => {
      if (!count) return;
      const data = new FormData(form);
      const names = ["estate", "development", "propertyType", "minPrice", "maxPrice", "bedrooms", "bathrooms", "status", "onShow"];
      const selectedLifestyle = form.querySelectorAll<HTMLInputElement>("[data-lifestyle-filter]:checked").length;
      const active = names.filter((name) => String(data.get(name) || "").trim() !== "").length + selectedLifestyle;
      count.textContent = String(active);
      if (summary) summary.textContent = `${active} active ${active === 1 ? "preference" : "preferences"}`;
    };
    const setMoreOpen = (open: boolean) => {
      if (!moreToggle || !morePanel) return;
      form.toggleAttribute("data-search-more-open", open);
      moreToggle.setAttribute("aria-expanded", String(open));
      morePanel.setAttribute("aria-hidden", String(!open));
      morePanel.toggleAttribute("inert", !open);
    };
    moreToggle?.addEventListener("click", () => setMoreOpen(moreToggle.getAttribute("aria-expanded") !== "true"));
    form.querySelectorAll<HTMLInputElement>('input[name="kind"]').forEach((input) => input.addEventListener("change", () => {
      if (!input.checked) return;
      rebuildPriceRanges(input.value);
      updateCount();
      if (form.classList.contains("nce-property-discovery")) form.requestSubmit();
    }));
    estate?.addEventListener("change", () => { updateDevelopments(); updateCount(); });
    minimumPrice?.addEventListener("change", constrainPriceRange);
    form.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input,select").forEach((field) => field.addEventListener("change", updateCount));
    resultsSort?.addEventListener("change", () => form.requestSubmit());
    updateDevelopments();
    constrainPriceRange();
    updateCount();
  });
}
function initEstatePropertyFilters(): void {
  document.querySelectorAll<HTMLFormElement>("[data-estate-property-filter]").forEach((form) => {
    const page = form.closest<HTMLElement>(".nce-estate-subsite-page--property");
    if (!page) return;
    const cards = Array.from(page.querySelectorAll<HTMLElement>("[data-estate-property-cards] .nce-listing-card"));
    const count = page.querySelector<HTMLElement>("[data-estate-filter-count]");
    const status = page.querySelector<HTMLElement>("[data-estate-filter-status]");
    const empty = page.querySelector<HTMLElement>("[data-estate-filter-empty]");
    const minimumPrice = form.querySelector<HTMLSelectElement>('[data-estate-price="min"]');
    const maximumPrice = form.querySelector<HTMLSelectElement>('[data-estate-price="max"]');
    const salePrices = [500000, 750000, 1000000, 1250000, 1500000, 2000000, 2500000, 3000000, 4000000, 5000000, 7500000, 10000000, 15000000, 20000000, 30000000, 50000000];
    const rentalPrices = [5000, 7500, 10000, 12500, 15000, 20000, 25000, 30000, 40000, 50000, 65000, 80000, 100000, 150000];
    const money = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 });

    const constrainPrices = () => {
      if (!minimumPrice || !maximumPrice) return;
      const minimum = Number(minimumPrice.value || 0);
      Array.from(maximumPrice.options).forEach((option) => { option.disabled = Boolean(option.value) && Number(option.value) < minimum; });
      if (maximumPrice.value && Number(maximumPrice.value) < minimum) maximumPrice.value = "";
    };
    const rebuildPrices = (kind: string) => {
      if (!minimumPrice || !maximumPrice) return;
      const values = kind === "rental" ? rentalPrices : salePrices;
      const rebuild = (select: HTMLSelectElement, placeholder: string) => {
        const previous = Number(select.value || 0);
        select.replaceChildren(new Option(placeholder, ""), ...values.map((price) => new Option(money.format(price), String(price))));
        select.value = values.includes(previous) ? String(previous) : "";
      };
      rebuild(minimumPrice, "No minimum");
      rebuild(maximumPrice, "No maximum");
      constrainPrices();
    };
    const apply = () => {
      const data = new FormData(form);
      const kind = String(data.get("kind") || "");
      const propertyType = String(data.get("propertyType") || "");
      const minimum = Number(data.get("minPrice") || 0);
      const maximum = Number(data.get("maxPrice") || 0);
      const bedrooms = Number(data.get("bedrooms") || 0);
      const bathrooms = Number(data.get("bathrooms") || 0);
      const features = data.getAll("feature").map(String);
      let visible = 0;

      cards.forEach((card) => {
        const price = Number(card.dataset.price || 0);
        const cardFeatures = (card.dataset.features || "").split(/\s+/).filter(Boolean);
        const matches = (!kind || card.dataset.kind === kind)
          && (!propertyType || card.dataset.propertyType === propertyType)
          && (!minimum || price >= minimum)
          && (!maximum || (price > 0 && price <= maximum))
          && (!bedrooms || Number(card.dataset.bedrooms || 0) >= bedrooms)
          && (!bathrooms || Number(card.dataset.bathrooms || 0) >= bathrooms)
          && features.every((feature) => cardFeatures.includes(feature) || cardFeatures.includes("pet"));
        card.hidden = !matches;
        card.setAttribute("aria-hidden", String(!matches));
        if (matches) visible += 1;
      });

      if (count) count.textContent = String(visible);
      if (status) status.textContent = `${visible} ${visible === 1 ? "property" : "properties"} shown`;
      if (empty) empty.hidden = visible > 0;
    };

    form.addEventListener("input", apply);
    form.addEventListener("change", (event) => {
      const target = event.target as HTMLInputElement | HTMLSelectElement;
      if (target.name === "kind") rebuildPrices(target.value);
      if (target === minimumPrice) constrainPrices();
      apply();
    });
    form.addEventListener("reset", () => window.requestAnimationFrame(() => { rebuildPrices(""); apply(); }));
    apply();
  });
}
function initPropertyEnquiryModal(): void {
  document.querySelectorAll<HTMLElement>("[data-nce-property-enquiry-modal]").forEach((modal) => {
    // Keep the enquiry above sticky site chrome and the WordPress toolbar,
    // matching the full-screen WhatsApp gate.
    document.body.append(modal);
    modal.style.zIndex = "2147483000";
    modal.style.isolation = "isolate";
    modal.style.transition = "visibility 0s";
    const openers = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-nce-enquiry-open]"));
    const closers = Array.from(modal.querySelectorAll<HTMLButtonElement>("[data-nce-enquiry-close]"));
    let returnFocus: HTMLElement | null = null;
    let closeTimer = 0;

    const focusable = (): HTMLElement[] => Array.from(modal.querySelectorAll<HTMLElement>('button:not([disabled]),a[href],input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter((element) => element.getClientRects().length > 0);
    const open = (opener: HTMLElement) => {
      window.clearTimeout(closeTimer);
      returnFocus = opener;
      modal.hidden = false;
      document.body.classList.add("nce-property-enquiry-open");
      window.requestAnimationFrame(() => {
        modal.setAttribute("data-open", "");
        modal.querySelector<HTMLButtonElement>(".nce-property-enquiry-modal__close")?.focus({ preventScroll: true });
      });
    };
    const close = () => {
      modal.removeAttribute("data-open");
      document.body.classList.remove("nce-property-enquiry-open");
      closeTimer = window.setTimeout(() => {
        modal.hidden = true;
        returnFocus?.focus();
      }, 220);
    };

    openers.forEach((opener) => opener.addEventListener("click", () => open(opener)));
    closers.forEach((closer) => closer.addEventListener("click", close));
    modal.addEventListener("keydown", (event) => {
      if (event.key === "Escape") { event.preventDefault(); close(); return; }
      if (event.key !== "Tab") return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0]!;
      const last = elements[elements.length - 1]!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  });
}

function initWhatsappGate(): void {
  document.querySelectorAll<HTMLElement>("[data-nce-whatsapp-gate]").forEach((modal) => {
    // Keep the gate outside property-page stacking contexts so its backdrop
    // always covers sticky site chrome and the WordPress toolbar immediately.
    document.body.append(modal);
    modal.style.zIndex = "2147483000";
    modal.style.isolation = "isolate";
    modal.style.transition = "visibility 0s";
    const form = modal.querySelector<HTMLFormElement>("[data-nce-whatsapp-gate-form]");
    const openers = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-nce-whatsapp-open]"));
    const closers = Array.from(modal.querySelectorAll<HTMLButtonElement>("[data-nce-whatsapp-close]"));
    if (!form || !openers.length) return;

    let returnFocus: HTMLElement | null = null;
    let closeTimer = 0;
    const focusable = (): HTMLElement[] => Array.from(modal.querySelectorAll<HTMLElement>('button:not([disabled]),a[href],input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter((element) => element.getClientRects().length > 0);
    const safeWhatsappUrl = (candidate: string): string => {
      try {
        const parsed = new URL(candidate);
        return parsed.protocol === "https:" && ["wa.me", "api.whatsapp.com"].includes(parsed.hostname) ? parsed.href : "";
      } catch { return ""; }
    };
    const open = (opener: HTMLElement) => {
      window.clearTimeout(closeTimer);
      returnFocus = opener;
      modal.hidden = false;
      document.body.classList.add("nce-whatsapp-gate-open");
      window.requestAnimationFrame(() => {
        modal.setAttribute("data-open", "");
        modal.querySelector<HTMLButtonElement>(".nce-property-enquiry-modal__close[data-nce-whatsapp-close]")?.focus({ preventScroll: true });
      });
    };
    const close = () => {
      modal.removeAttribute("data-open");
      document.body.classList.remove("nce-whatsapp-gate-open");
      closeTimer = window.setTimeout(() => {
        modal.hidden = true;
        returnFocus?.focus();
      }, 220);
    };

    openers.forEach((opener) => opener.addEventListener("click", () => open(opener)));
    closers.forEach((closer) => closer.addEventListener("click", close));
    modal.addEventListener("keydown", (event) => {
      if (event.key === "Escape") { event.preventDefault(); close(); return; }
      if (event.key !== "Tab") return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0]!;
      const last = elements[elements.length - 1]!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const data = new FormData(form);
      if (data.get("website")) return;
      const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
      const status = form.querySelector<HTMLElement>("[data-form-status]");
      const optional = (name: string): string | undefined => String(data.get(name) || "").trim() || undefined;
      const propertyMessage = String(data.get("message") || "WhatsApp property enquiry");
      const whatsappWindow = window.open("about:blank", "nce-whatsapp");
      if (whatsappWindow) {
        try {
          whatsappWindow.opener = null;
          whatsappWindow.document.title = "Opening WhatsApp…";
          whatsappWindow.document.body.textContent = "Saving your enquiry before opening WhatsApp…";
        } catch { /* The destination can still be assigned below. */ }
      }
      if (submit) submit.disabled = true;
      if (status) { status.dataset.status = "loading"; status.textContent = "Saving your details securely…"; }

      try {
        const receipt = await sendEnquiry({
          type: "property",
          name: String(data.get("name") || ""),
          email: String(data.get("email") || ""),
          phone: String(data.get("phone") || ""),
          message: propertyMessage,
          ...(optional("listingId") ? { listingId: optional("listingId") } : {}),
          ...(optional("estateId") ? { estateId: optional("estateId") } : {}),
          ...(optional("developmentId") ? { developmentId: optional("developmentId") } : {}),
          ...(optional("agentId") ? { agentId: optional("agentId") } : {}),
          pageUrl: window.location.href,
          formVariant: "whatsapp-gate",
          consent: { accepted: data.get("consent") === "1", languageVersion: "privacy-popia-whatsapp-v1", marketingOptIn: false },
          attribution: attribution(), honeypot: "",
        });
        if (receipt.data.sharedAwaitingClaim) {
          if (whatsappWindow && !whatsappWindow.closed) whatsappWindow.close();
          if (status) { status.dataset.status = "success"; status.textContent = "Your WhatsApp contact request is saved. An agent assigned to this property will claim the enquiry and contact you."; }
          window.dispatchEvent(new CustomEvent("nce:enquiry", { detail: { type: "property", channel: "whatsapp", duplicate: receipt.data.duplicate } }));
          form.reset();
          return;
        }
        const whatsappUrl = safeWhatsappUrl(receipt.data.whatsappUrl || "");
        if (!whatsappUrl) {
          if (whatsappWindow && !whatsappWindow.closed) whatsappWindow.close();
          if (status) { status.dataset.status = "success"; status.textContent = "Your enquiry is saved. The listing agent will contact you; WhatsApp is not available for this listing right now."; }
          return;
        }
        if (status) { status.dataset.status = "success"; status.textContent = "Saved. Opening WhatsApp…"; }
        window.dispatchEvent(new CustomEvent("nce:enquiry", { detail: { type: "property", channel: "whatsapp", duplicate: receipt.data.duplicate } }));
        form.reset();
        close();
        if (whatsappWindow && !whatsappWindow.closed) whatsappWindow.location.replace(whatsappUrl);
        else window.location.assign(whatsappUrl);
      } catch {
        if (whatsappWindow && !whatsappWindow.closed) whatsappWindow.close();
        if (status) { status.dataset.status = "error"; status.textContent = "We could not save your details. Please check them and try again; WhatsApp has not been opened yet."; }
      } finally { if (submit) submit.disabled = false; }
    });
  });
}

function initListingOnboarding(): void {
  document.querySelectorAll<HTMLElement>("[data-nce-listing-modal]").forEach((modal) => {
    const openers = Array.from(document.querySelectorAll<HTMLButtonElement>(`[data-nce-listing-open="${modal.id}"]`));
    const closers = Array.from(modal.querySelectorAll<HTMLButtonElement>("[data-nce-listing-close]"));
    let returnFocus: HTMLElement | null = null;
    let closeTimer = 0;

    const focusable = (): HTMLElement[] => Array.from(modal.querySelectorAll<HTMLElement>('button:not([disabled]),a[href],input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter((element) => element.getClientRects().length > 0);
    const open = (opener: HTMLElement) => {
      window.clearTimeout(closeTimer);
      returnFocus = opener;
      modal.hidden = false;
      document.body.classList.add("nce-listing-onboarding-open");
      window.requestAnimationFrame(() => {
        modal.setAttribute("data-open", "");
        modal.querySelector<HTMLButtonElement>(".nce-listing-onboarding-modal__close")?.focus();
      });
    };
    const close = () => {
      modal.removeAttribute("data-open");
      document.body.classList.remove("nce-listing-onboarding-open");
      closeTimer = window.setTimeout(() => {
        modal.hidden = true;
        returnFocus?.focus();
      }, 220);
    };

    openers.forEach((opener) => opener.addEventListener("click", () => open(opener)));
    closers.forEach((closer) => closer.addEventListener("click", close));
    modal.addEventListener("keydown", (event) => {
      if (event.key === "Escape") { event.preventDefault(); close(); return; }
      if (event.key !== "Tab") return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0]!;
      const last = elements[elements.length - 1]!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  });
}
function initCookiePreferences(): void {
  document.querySelector<HTMLButtonElement>("[data-cookie-preferences]")?.addEventListener("click", () => { const existing = localStorage.getItem("nceCookiePreferences"); const enable = window.confirm(`Optional, consent-aware analytics are currently ${existing === "analytics" ? "enabled" : "disabled"}. Select OK to enable them, or Cancel to keep only essential storage.`); try { localStorage.setItem("nceCookiePreferences", enable ? "analytics" : "essential"); } catch { /* Consent preference can be held in memory when storage fails. */ } const status = document.querySelector<HTMLElement>("[data-cookie-status]"); if (status) status.textContent = enable ? "Optional analytics enabled. You can change this choice at any time." : "Only essential browser storage is enabled."; window.dispatchEvent(new CustomEvent("nce:consent", { detail: { analytics: enable } })); });
}
function initPropertyReels(): void {
  document.querySelectorAll<HTMLElement>("[data-nce-reels]").forEach((experience) => {
    const form = experience.querySelector<HTMLFormElement>("[data-reels-search]");
    const query = experience.querySelector<HTMLInputElement>("[data-reels-query]");
    const feed = experience.querySelector<HTMLElement>("[data-reels-feed]");
    const empty = experience.querySelector<HTMLElement>("[data-reels-empty]");
    const count = experience.querySelector<HTMLElement>("[data-reels-count]");
    const status = experience.querySelector<HTMLElement>("[data-reels-status]");
    const progress = experience.querySelector<HTMLElement>("[data-reels-progress]");
    if (!feed) return;

    const allReels = Array.from(feed.querySelectorAll<HTMLElement>("[data-reel]"));
    const videos = () => allReels.filter((reel) => !reel.hidden);
    const syncProgress = (active?: HTMLElement) => {
      if (!progress) return;
      progress.replaceChildren();
      videos().forEach((reel, index) => {
        const marker = document.createElement("button");
        marker.type = "button";
        marker.setAttribute("aria-label", `Open reel ${index + 1}`);
        marker.setAttribute("aria-current", String(reel === active));
        marker.addEventListener("click", () => reel.scrollIntoView({ behavior: "smooth", block: "start" }));
        progress.append(marker);
      });
    };
    const pauseOthers = (current: HTMLVideoElement) => {
      allReels.forEach((reel) => { const video = reel.querySelector<HTMLVideoElement>("[data-reel-video]"); if (video && video !== current) video.pause(); });
    };
    const observer = new IntersectionObserver((entries) => {
      const active = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!active || active.intersectionRatio < .55) return;
      const reel = active.target as HTMLElement;
      const video = reel.querySelector<HTMLVideoElement>("[data-reel-video]");
      if (video) { pauseOthers(video); void video.play().catch(() => undefined); }
      syncProgress(reel);
    }, { root: feed, threshold: [.55, .8] });
    allReels.forEach((reel) => {
      observer.observe(reel);
      const video = reel.querySelector<HTMLVideoElement>("[data-reel-video]");
      const sound = reel.querySelector<HTMLButtonElement>("[data-reel-sound]");
      sound?.addEventListener("click", () => {
        if (!video) return;
        video.muted = !video.muted;
        sound.querySelector("span")!.textContent = video.muted ? "Sound off" : "Sound on";
        sound.setAttribute("aria-label", video.muted ? "Turn sound on" : "Mute video");
        if (!video.muted) { pauseOthers(video); void video.play().catch(() => undefined); }
      });
    });

    const filter = () => {
      const value = (query?.value || "").toLocaleLowerCase().trim();
      const words = value.split(/\s+/).filter((word) => word.length > 1);
      allReels.forEach((reel) => {
        const tags = `${reel.dataset.reelTags || ""} ${reel.textContent || ""}`.toLocaleLowerCase();
        reel.hidden = words.length > 0 && !words.some((word) => tags.includes(word));
        reel.querySelector<HTMLVideoElement>("[data-reel-video]")?.pause();
      });
      const matches = videos();
      if (empty) empty.hidden = matches.length > 0;
      if (count) count.textContent = `${matches.length} ${matches.length === 1 ? "video" : "videos"}`;
      if (status) status.textContent = value ? (matches.length ? `Your reel for “${value}” is ready.` : `No demo videos match “${value}”.`) : "Showing a curated North Coast reel.";
      syncProgress(matches[0]);
      matches[0]?.scrollIntoView({ behavior: "smooth", block: "start" });
      const firstVideo = matches[0]?.querySelector<HTMLVideoElement>("[data-reel-video]");
      if (firstVideo) void firstVideo.play().catch(() => undefined);
    };
    form?.addEventListener("submit", (event) => { event.preventDefault(); filter(); });
    experience.querySelectorAll<HTMLButtonElement>("[data-reels-prompt]").forEach((button) => button.addEventListener("click", () => { if (query) query.value = button.dataset.reelsPrompt || ""; filter(); }));
    feed.addEventListener("keydown", (event) => {
      if (!["ArrowDown", "ArrowUp", "PageDown", "PageUp"].includes(event.key)) return;
      event.preventDefault();
      const visible = videos();
      const current = visible.findIndex((reel) => Math.abs(reel.getBoundingClientRect().top - feed.getBoundingClientRect().top) < feed.clientHeight / 2);
      const direction = ["ArrowDown", "PageDown"].includes(event.key) ? 1 : -1;
      visible[Math.max(0, Math.min(visible.length - 1, current + direction))]?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    syncProgress(allReels[0]);
  });
}
function initEstateComparison(): void {
  document.querySelectorAll<HTMLElement>("[data-nce-estate-compare]").forEach((comparison) => {
    const search = comparison.querySelector<HTMLInputElement>("[data-compare-search]");
    const rows = Array.from(comparison.querySelectorAll<HTMLTableRowElement>("[data-nce-compare-row]"));
    const filters = Array.from(comparison.querySelectorAll<HTMLButtonElement>("[data-compare-filter]"));
    const status = comparison.querySelector<HTMLElement>("[data-compare-status]");
    const empty = comparison.querySelector<HTMLElement>("[data-compare-empty]");
    let activeFilter = "all";

    const apply = () => {
      const query = (search?.value || "").trim().toLocaleLowerCase();
      let visible = 0;

      rows.forEach((row) => {
        const haystack = row.dataset.compareSearch || "";
        const tags = (row.dataset.compareTags || "").split(/\s+/).filter(Boolean);
        const matchesSearch = !query || haystack.includes(query);
        const matchesFilter = activeFilter === "all" || tags.includes(activeFilter);
        row.hidden = !(matchesSearch && matchesFilter);
        if (!row.hidden) visible += 1;
      });

      if (status) status.textContent = `${visible} ${visible === 1 ? "estate" : "estates"} shown`;
      if (empty) empty.hidden = visible > 0;
    };

    search?.addEventListener("input", apply);
    filters.forEach((filter) => {
      filter.addEventListener("click", () => {
        activeFilter = filter.dataset.compareFilter || "all";
        filters.forEach((candidate) => candidate.setAttribute("aria-pressed", String(candidate === filter)));
        apply();
      });
    });
    apply();
  });
}
function boot(): void { initEnquiries(); initAccountLite(); initShortlist(); initDeferredMap(); initPropertyReturn(initInfiniteListings(renderFavouriteButtons)); initPropertySearch(); initEstatePropertyFilters(); initPropertyEnquiryModal(); initWhatsappGate(); initListingOnboarding(); initPropertyReels(); initEstateComparison(); initCookiePreferences(); }
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true }); else boot();
